import { genericTokenIcon } from './config';

/**
 * Filter graph data based on the selected view mode
 * 
 * @param {Object} graphData - The original graph data with nodes and links
 * @param {string} viewMode - Current view mode ('all', 'token_connected', 'users_only', 'tokens_only')
 * @returns {Object} - Filtered graph data with nodes and links based on the view mode
 */
export function filterGraphDataByViewMode(graphData, viewMode) {
  if (!graphData || !graphData.nodes || !graphData.links) {
    console.error('Invalid graph data provided to filterGraphDataByViewMode');
    return { nodes: [], links: [] };
  }

  // If viewing all nodes, return the original data
  if (viewMode === 'all') {
    return graphData;
  }

  let filteredNodes = [];
  
  // Filter nodes based on view mode
  if (viewMode === 'token_connected') {
    // Keep tokens and users connected to tokens
    filteredNodes = graphData.nodes.filter(node => 
      node.nodeType === 'token' || node.nodeType === 'connected_user'
    );
  } else if (viewMode === 'users_only') {
    // Keep only users (both connected and isolated)
    filteredNodes = graphData.nodes.filter(node =>
      node.nodeType === 'connected_user' || node.nodeType === 'isolated_user'
    );
  } else if (viewMode === 'tokens_only') {
    // Keep only tokens that don't have the generic token icon
    filteredNodes = graphData.nodes.filter(node =>
      node.nodeType === 'token' && node.icon && node.icon !== genericTokenIcon
    );
  }

  // Get IDs of filtered nodes for link filtering
  const filteredNodeIds = new Set(filteredNodes.map(node => node.id));
  
  // Filter links to only include connections between filtered nodes
  const filteredLinks = graphData.links.filter(link => {
    if (!link) return false;
    const src = (typeof link.source === 'object' && link.source) ? link.source.id : link.source;
    const tgt = (typeof link.target === 'object' && link.target) ? link.target.id : link.target;
    return filteredNodeIds.has(src) && filteredNodeIds.has(tgt);
  });

  return {
    nodes: filteredNodes,
    links: filteredLinks
  };
}

/**
 * Get visible graph data based on the selected view mode and other filters
 * 
 * @param {Object} originalGraphData - The original graph data with nodes and links
 * @param {string} viewMode - Current view mode ('all', 'token_connected', 'users_only')
 * @param {boolean} filterUser - Whether to filter users
 * @param {boolean} showNewTokens - Whether to show new tokens
 * @returns {Object} - Filtered graph data with nodes and links
 */
export function getVisibleGraphData(originalGraphData, viewMode, filterUser, showNewTokens) {
  // First filter by view mode
  let filteredData = filterGraphDataByViewMode(originalGraphData, viewMode);
  
  // Apply additional filters if needed
  if (filterUser || showNewTokens) {
    // Implement additional filtering logic here
    // For example, filter by user or show only new tokens
  }
  
  return filteredData;
}
